import type { Corners } from './embedConfig'

export const VIOLET = '#7839EE'
const WHITE = '#FFFFFF'
export const SIDEBAR_DARK_FOREGROUND = '#111827'
const SIDEBAR_LUMINANCE_THRESHOLD = 0.179

export type EmbedTheme = {
  brand: string
  brandSoft: string
  brandText: string
  brandInk: string
}

export function parseHex(input: string) {
  const match = /^#?([0-9a-f]{6})$/i.exec(input.trim())
  return match ? `#${match[1].toUpperCase()}` : null
}

function hexToRgb(hex: string) {
  const parsed = parseHex(hex)
  if (!parsed) return null
  const value = parseInt(parsed.slice(1), 16)
  return { r: (value >> 16) & 255, g: (value >> 8) & 255, b: value & 255 }
}

function relativeLuminance(hex: string) {
  const rgb = hexToRgb(hex)
  if (!rgb) return null
  const linear = (channel: number) => {
    const c = channel / 255
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * linear(rgb.r) + 0.7152 * linear(rgb.g) + 0.0722 * linear(rgb.b)
}

export function isReadableOnWhite(hex: string) {
  const luminance = relativeLuminance(hex)
  return luminance !== null && luminance <= SIDEBAR_LUMINANCE_THRESHOLD
}

function tint(hex: string, towardsWhite: number) {
  const rgb = hexToRgb(hex)
  if (!rgb) return '#F4F4F5'
  const mix = (channel: number) => Math.round(channel + (255 - channel) * towardsWhite)
  return `rgb(${mix(rgb.r)}, ${mix(rgb.g)}, ${mix(rgb.b)})`
}

export function defaultBrand(sidebarColor: string | null | undefined) {
  const hex = `#${(sidebarColor ?? '').replace(/^#/, '')}`
  return isReadableOnWhite(hex) ? hex.toUpperCase() : VIOLET
}

export function embedTheme(brand: string): EmbedTheme {
  const readable = isReadableOnWhite(brand)
  return {
    brand,
    brandSoft: tint(brand, 0.88),
    brandText: readable ? WHITE : SIDEBAR_DARK_FOREGROUND,
    brandInk: readable ? brand : SIDEBAR_DARK_FOREGROUND,
  }
}

export const CORNER_RADII: Record<Corners, { radius: string; radiusSm: string }> = {
  rounded: { radius: '1rem', radiusSm: '0.5rem' },
  square: { radius: '2px', radiusSm: '2px' },
}
